import Form from "rc-field-form";
import { useAppContext } from "@src/autoDebit/AppContext";
import message from "@src/autoDebit/message";
import TextInput from "../TextInput/TextInput";
import ItemRender from "../ItemRender";
import styles from "./Register.module.css";
import openLink from "@src/assets/autoDebit/openWindow.png";
import Radio from "../Radio/Radio";
import { useMemo, useRef, useState } from "react";
import DateSelect from "../DateSelect/DateSelect";
import dayjs from "dayjs";
import { fetchFormData } from "./fetchForm";
import Button from "../Button";
import { dateFormat } from "@src/applicationForm/utils/dateHelper";
import { telephoneAutoDebit } from "@src/utils/patternHelper";
import { isOnlySpaces, isValid2ByteCharacters } from "./isValid2ByteCharacters";
import { isTimeInRange } from "@src/utils/timeInRange";
import { getEnumByLocale } from "@src/utils/languageHelper";

function SetCookie(name: string, value: string) {
  document.cookie = name + "=" + value + ";path=/";
}

const Register = () => {
  const { intl, errorNotifier, locale } = useAppContext();
  const [formRef] = Form.useForm();
  const [isCorporate, setIsCorporate] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [failedMessage, setFailedMessage] = useState("");
  const storeRef = useRef<boolean>();
  const privacyLink = useMemo(() => {
    if (locale === "ja") return "/privacy-policy";
    return `/${locale}/privacy-policy`;
  }, [locale]);

  const ContractClassificationOption = useMemo(() => {
    return [
      {
        value: "individual",
        label: message["ContractClassification.Option1"],
      },
      {
        value: "corporate",
        label: message["ContractClassification.Option2"],
      },
    ];
  }, []);

  const birthMaxDate = useMemo(() => {
    const birthOf16Year = dayjs().subtract(16, "year");
    return birthOf16Year.format(dateFormat);
  }, []);

  const birthMinDate = useMemo(() => {
    const birthOf100Year = dayjs().subtract(100, "year");
    return birthOf100Year.format(dateFormat);
  }, []);

  const isNewYear = isTimeInRange(
    "2023-12-27T18:00+09:00",
    "2024-01-05T08:59+09:00"
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.title}>
        {intl.formatMessage(
          isNewYear ? message.NotificationTitle : message.Step1
        )}
      </div>
      <div className={styles.content}>
        {isNewYear ? (
          <div className={styles.notification}>
            {intl.formatMessage(message.Notification)}
          </div>
        ) : (
          <>
            <div className={styles.description}>
              {intl.formatMessage(message.FormTip)}
            </div>

            <Form
              form={formRef}
              onValuesChange={(values) => {
                if (values.isCorporate) {
                  setIsCorporate(values.isCorporate === "corporate");
                }
              }}
            >
              <ItemRender
                id="applicationNumber"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage(
                      message["ApplicationNumber.Error.Required"]
                    ),
                  },
                  {
                    pattern: /^\d{5,7}$/,
                    message: intl.formatMessage(
                      message["ApplicationNumber.Error.Other"]
                    ),
                  },
                ]}
                renderProps={(topLevelProps) => (
                  <TextInput
                    {...topLevelProps}
                    title={message["ApplicationNumber.Title"]}
                    placeholder={message["ApplicationNumber.Placeholder"]}
                  ></TextInput>
                )}
              ></ItemRender>
              <ItemRender
                id="phone"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage(
                      message["Telephone.Error.Required"]
                    ),
                  },
                  {
                    pattern: telephoneAutoDebit,
                    message: intl.formatMessage(
                      message["Telephone.Error.Other"]
                    ),
                  },
                ]}
                renderProps={(topLevelProps) => (
                  <TextInput
                    {...topLevelProps}
                    title={message["Telephone.Title"]}
                    autoComplete="tel"
                    name="telephone"
                    placeholder={message["Telephone.Placeholder"]}
                  ></TextInput>
                )}
              ></ItemRender>

              <ItemRender
                id="name"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value || isOnlySpaces(value)) {
                        return Promise.reject(
                          intl.formatMessage(message["FullName.Error.Required"])
                        );
                      }
                      if (isValid2ByteCharacters(value) && value.length <= 30) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        intl.formatMessage(message["FullName.Error.Other"])
                      );
                    },
                  },
                ]}
                renderProps={(topLevelProps) => (
                  <TextInput
                    {...topLevelProps}
                    autoComplete="name"
                    name="name"
                    title={message["FullName.Title"]}
                    placeholder={message["FullName.Placeholder"]}
                  ></TextInput>
                )}
              ></ItemRender>

              <ItemRender
                id="isCorporate"
                initialValue="individual"
                renderProps={(topLevelProps) => (
                  <Radio
                    {...topLevelProps}
                    title={message.ContractClassification}
                    option={ContractClassificationOption}
                  ></Radio>
                )}
              ></ItemRender>

              <ItemRender
                id="dateOfBirth"
                dependencies={["isCorporate"]}
                rules={
                  isCorporate
                    ? []
                    : [
                        {
                          required: true,
                          message: intl.formatMessage(
                            message["Birth.Error.Required"]
                          ),
                        },
                      ]
                }
                renderProps={(topLevelProps) => (
                  <DateSelect
                    {...topLevelProps}
                    disabled={
                      formRef.getFieldValue("isCorporate") === "corporate"
                    }
                    title={message.Birth}
                    dateOption={{
                      maxDate: birthMaxDate,
                      minDate: birthMinDate,
                    }}
                  ></DateSelect>
                )}
              ></ItemRender>

              <a href={privacyLink} className={styles.privacy} target="_blank">
                <span>{intl.formatMessage(message.Privacy)}</span>
                <img src={openLink} alt="" />
              </a>

              {isFailed && failedMessage && (
                <div className={styles.failed}>{failedMessage}</div>
              )}

              <Button
                style={{ marginTop: "24px" }}
                onClick={() => {
                  if (storeRef.current) return;
                  storeRef.current = true;
                  setIsFailed(false);
                  setFailedMessage("");
                  formRef
                    .validateFields()
                    .then(() => {
                      const allValues = formRef.getFieldsValue();
                      const param = { ...allValues };
                      param.isCorporate = param.isCorporate === "corporate";
                      param.lang = getEnumByLocale(locale);
                      if (param.dateOfBirth) {
                        param.dateOfBirth = param.dateOfBirth.replace(
                          /\//g,
                          ""
                        );
                      }
                      return fetchFormData(param);
                    })
                    .then((res) => {
                      if (
                        "kfrequest" in res &&
                        "url" in res &&
                        typeof res.url === "string" &&
                        typeof res.kfrequest === "string"
                      ) {
                        SetCookie("language_at_login", locale);
                        const formEle =
                          document.querySelector<HTMLFormElement>("#FakeForm");
                        const valEle =
                          document.querySelector<HTMLInputElement>(
                            "#kfrequest"
                          );
                        if (!formEle || !valEle) return;
                        valEle.value = res.kfrequest;
                        formEle.action = res.url;
                        // redirect to other page
                        formEle.submit();
                        return;
                      }
                      if (
                        "error_message" in res &&
                        typeof res.error_message === "string"
                      ) {
                        setIsFailed(true);
                        setFailedMessage(res.error_message);
                      } else {
                        throw new Error("AutoDebit: Register failed");
                      }
                    })
                    .catch((error) => {
                      console.warn(error);
                      if (!(error instanceof Error)) return;
                      setIsFailed(true);
                      setFailedMessage(intl.formatMessage(message.Failed));
                      errorNotifier.notify({
                        error,
                        msg: "Failed to fetch submit auto debit",
                      });
                    })
                    .finally(() => (storeRef.current = false));
                }}
              >
                {intl.formatMessage(message.Submit)}
              </Button>
            </Form>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;
